import { type JSX, useCallback, useEffect, useState } from 'react';
import {
  ActionIcon,
  AppShell,
  Badge,
  Burger,
  Button,
  Container,
  Divider,
  Group,
  Loader,
  NavLink,
  Paper,
  ScrollArea,
  Stack,
  Text,
  Tooltip,
  useMantineColorScheme,
} from '@mantine/core';
import { useDisclosure } from '@mantine/hooks';
import {
  IconBook2,
  IconChartHistogram,
  IconGauge,
  IconHelpCircle,
  IconMessages,
  IconId,
  IconSend,
  IconSitemap,
  IconTags,
  IconUrgent,
  IconUsers,
} from '@tabler/icons-react';
import { type Pendencias, type Sessao, ErroApi, api } from './lib/api.js';
import { Login } from './componentes/Login.js';
import { Home } from './componentes/Home.js';
import { ListaFaq } from './componentes/ListaFaq.js';
import { FilaLacunas } from './componentes/FilaLacunas.js';
import { FilaTransbordos } from './componentes/FilaTransbordos.js';
import { Menu } from './componentes/Menu.js';
import { Metricas } from './componentes/Metricas.js';
import { Conversas } from './componentes/Conversas.js';
import { DetalheTecnico } from './componentes/DetalheTecnico.js';
import { Usuarios } from './componentes/Usuarios.js';
import { Pessoas } from './componentes/Pessoas.js';
import { Envios } from './componentes/Envios.js';
import { Tags } from './componentes/Tags.js';
import { AceitarConvite } from './componentes/AceitarConvite.js';
import { TrocarSenha } from './componentes/TrocarSenha.js';

/**
 * Shell do painel: navegação LATERAL fixa.
 *
 * As abas horizontais não cabiam mais — sete seções já se apertavam numa tela
 * de balcão, e a oitava (a home) não entraria. A lateral resolve isso e abre
 * espaço para o que as abas não tinham: um bloco de contexto embaixo, sempre
 * visível, dizendo o que está vivo AGORA. Quem está atendendo não precisa
 * trocar de tela para saber que há alguém esperando.
 *
 * Continua sem rota e sem biblioteca de estado: a seção é um `useState`, como
 * era com as abas.
 */

type Seccao =
  | 'home'
  | 'transbordos'
  | 'conversas'
  | 'faqs'
  | 'lacunas'
  | 'menu'
  | 'metricas'
  | 'pessoas'
  | 'envios'
  | 'tags'
  | 'usuarios';

/** Token de convite na URL: `/?convite=...`. */
function tokenDeConvite(): string | null {
  return new URLSearchParams(window.location.search).get('convite');
}

/** Recarrega os contadores sozinho. Um minuto é curto para a fila e longo o
 *  bastante para não martelar o servidor com a tela aberta no balcão. */
const INTERVALO_PENDENCIAS_MS = 60_000;

const LARGURA_LATERAL = 236;

/** Tamanho e traço dos ícones da lateral. Um lugar só, para não divergirem. */
const ICONE = { size: 20, stroke: 1.5 } as const;

/** Alterna claro/escuro. A escolha fica no localStorage (preferência de
 *  aparelho, não de usuário — ver `CHAVE_TEMA`). */
function BotaoDeTema(): JSX.Element {
  const { colorScheme, setColorScheme } = useMantineColorScheme();
  const escuro = colorScheme === 'dark';
  return (
    <Tooltip label={escuro ? 'Tema claro' : 'Tema escuro'}>
      <ActionIcon
        variant="default"
        size="lg"
        aria-label={escuro ? 'Mudar para tema claro' : 'Mudar para tema escuro'}
        onClick={() => setColorScheme(escuro ? 'light' : 'dark')}
      >
        {escuro ? '☀' : '☾'}
      </ActionIcon>
    </Tooltip>
  );
}

export function App(): JSX.Element {
  const [sessao, setSessao] = useState<Sessao | null>(null);
  const [carregando, setCarregando] = useState(true);
  // A home abre primeiro: ela responde "como estamos" e aponta para a fila
  // quando há alguém esperando.
  const [secao, setSecao] = useState<Seccao>('home');
  const [pendencias, setPendencias] = useState<Pendencias | null>(null);
  /**
   * Contato a abrir ao chegar em Conversas.
   *
   * Endereçado pelo atendimento, como todo o resto do painel — o servidor
   * resolve o telefone. Vem da fila de transbordos e da tela inicial.
   */
  const [conversaAlvo, setConversaAlvo] = useState<string | null>(null);
  /**
   * Atendimento aberto na visão de auditoria.
   *
   * Sobreposto à conversa, porque é um desvio de dentro da leitura — não uma
   * seção à parte, que era justamente o que confundia a navegação.
   */
  const [atendimentoTecnico, setAtendimentoTecnico] = useState<string | null>(null);
  const [trocandoSenha, { open: abrirSenha, close: fecharSenha }] = useDisclosure(false);
  const [menuAberto, { toggle: alternarMenu, close: fecharMenu }] = useDisclosure(false);
  /** Lido uma vez: o convite vem na URL e é consumido ao definir a senha. */
  const [convite, setConvite] = useState<string | null>(tokenDeConvite);

  // Na abertura, pergunta ao servidor se o cookie ainda vale — em vez de
  // guardar o estado de login no localStorage, que mentiria depois que a
  // sessão expirasse ou o usuário fosse desativado.
  useEffect(() => {
    api
      .get<Sessao>('/admin/eu')
      .then(setSessao)
      .catch((e: unknown) => {
        if (!(e instanceof ErroApi && e.naoAutenticado)) console.error(e);
      })
      .finally(() => setCarregando(false));
  }, []);

  const carregarPendencias = useCallback(() => {
    if (!sessao) return;
    void api
      .get<Pendencias>('/admin/pendencias')
      .then(setPendencias)
      .catch(() => undefined);
  }, [sessao]);

  useEffect(() => {
    carregarPendencias();
    const timer = setInterval(carregarPendencias, INTERVALO_PENDENCIAS_MS);
    return () => clearInterval(timer);
  }, [carregarPendencias]);

  async function sair(): Promise<void> {
    await api.post('/admin/logout').catch(() => undefined);
    setSessao(null);
  }

  /** Troca de seção e fecha o menu do celular. */
  function ir(destino: Seccao): void {
    setSecao(destino);
    fecharMenu();
  }

  /** Abre a conversa de um contato, vindo de qualquer outra tela. */
  function verConversa(atendimentoId: string): void {
    setConversaAlvo(atendimentoId);
    setSecao('conversas');
    fecharMenu();
  }

  if (carregando) {
    return (
      <Group justify="center" mt="xl">
        <Loader size="sm" />
        <Text c="dimmed">Carregando…</Text>
      </Group>
    );
  }

  /**
   * O convite vem ANTES do login: quem abre o link ainda não tem senha, e
   * mandá-lo para a tela de login seria um beco sem saída.
   */
  if (convite) {
    return (
      <AceitarConvite
        token={convite}
        aoEntrar={() => {
          // Limpa o token da barra de endereços: ele já foi queimado, e um
          // link no histórico que não funciona mais só confunde.
          window.history.replaceState(null, '', window.location.pathname);
          setConvite(null);
          setCarregando(true);
          api
            .get<Sessao>('/admin/eu')
            .then(setSessao)
            .catch(() => undefined)
            .finally(() => setCarregando(false));
        }}
      />
    );
  }

  if (!sessao) return <Login aoEntrar={setSessao} />;

  /** Zero não mostra badge: um "0" é ruído, não informação. */
  function contador(valor: number | undefined, urgente = false): JSX.Element | null {
    if (valor === undefined || valor === 0) return null;
    return (
      <Badge size="sm" circle variant="filled" color={urgente ? 'red' : 'gray'}>
        {valor}
      </Badge>
    );
  }

  /**
   * Ícone de cada seção.
   *
   * Está aqui, junto do rótulo e da descrição, porque é parte de como a seção
   * se apresenta — não enfeite aplicado por fora. Traço de 1,5 para acompanhar
   * o peso do texto; 20px para caber na linha sem empurrar a descrição.
   */
  const secoes: {
    id: Seccao;
    rotulo: string;
    descricao: string;
    icone: JSX.Element;
    badge?: JSX.Element | null;
  }[] = [
    { id: 'home', rotulo: 'Início', descricao: 'como estamos', icone: <IconGauge {...ICONE} /> },
    {
      id: 'transbordos',
      rotulo: 'Transbordos',
      descricao: 'quem está esperando',
      icone: <IconUrgent {...ICONE} />,
      badge: contador(pendencias?.transbordos, true),
    },
    {
      id: 'conversas',
      rotulo: 'Conversas',
      descricao: 'contatos e histórico',
      icone: <IconMessages {...ICONE} />,
      badge: contador(pendencias?.conversasPausadas),
    },
    {
      id: 'faqs',
      rotulo: 'FAQs',
      descricao: 'a base de respostas',
      icone: <IconBook2 {...ICONE} />,
      badge: contador(pendencias?.reindexacao),
    },
    {
      id: 'lacunas',
      rotulo: 'Lacunas',
      descricao: 'perguntas sem resposta',
      icone: <IconHelpCircle {...ICONE} />,
      badge: contador(pendencias?.lacunas),
    },
    { id: 'menu', rotulo: 'Menu', descricao: 'a árvore de atendimento', icone: <IconSitemap {...ICONE} /> },
    { id: 'metricas', rotulo: 'Métricas', descricao: 'calibração dos limites', icone: <IconChartHistogram {...ICONE} /> },
    {
      id: 'pessoas',
      rotulo: 'Pessoas',
      descricao: 'quem o bot reconhece',
      icone: <IconId {...ICONE} />,
    },
    // Envios vem DEPOIS de Pessoas, e a ordem é a do fluxo: primeiro se vê
    // quem o bot reconhece (e quantos têm celular utilizável), depois se
    // monta a lista de quem vai receber.
    {
      id: 'envios',
      rotulo: 'Envios',
      descricao: 'avisos e campanhas',
      icone: <IconSend {...ICONE} />,
    },
    {
      id: 'tags',
      rotulo: 'Tags',
      descricao: 'recortes de lista',
      icone: <IconTags {...ICONE} />,
    },
    // A seção só aparece para admin; as rotas também só respondem a ele.
    ...(sessao.capacidades.administrar
      ? [
          {
            id: 'usuarios' as Seccao,
            rotulo: 'Usuários',
            descricao: 'quem tem acesso',
            icone: <IconUsers {...ICONE} />,
          },
        ]
      : []),
  ];

  const atual = secoes.find((s) => s.id === secao);

  return (
    <AppShell
      header={{ height: 56 }}
      navbar={{
        width: LARGURA_LATERAL,
        breakpoint: 'sm',
        // No celular a lateral vira gaveta; no desktop ela é fixa e não some.
        collapsed: { mobile: !menuAberto },
      }}
      padding="lg"
    >
      <AppShell.Header>
        <Group h="100%" px="md" gap="sm" wrap="nowrap">
          <Burger opened={menuAberto} onClick={alternarMenu} hiddenFrom="sm" size="sm" />
          {/*
            A marca ACOMPANHA o título, não o substitui.

            `alt=""` de propósito: o texto ao lado já diz “Curadoria ·
            Aeroclube de Presidente Prudente”, e dar um texto alternativo à
            logo faria o leitor de tela anunciar o nome duas vezes. Imagem
            decorativa é a classificação correta aqui.

            `width`/`height` explícitos na proporção real (330×132) reservam o
            espaço antes de a imagem chegar — sem isso o cabeçalho pula no
            primeiro carregamento.
          */}
          <img
            src="/marca/logo-acpp.png"
            alt=""
            width={70}
            height={28}
            style={{ display: 'block', flexShrink: 0 }}
          />
          <Text fw={600}>Curadoria</Text>
          <Text size="sm" c="dimmed" visibleFrom="sm">
            · Aeroclube de Presidente Prudente
          </Text>

          <Group gap="xs" ml="auto" wrap="nowrap">
            <Text size="sm" c="dimmed" visibleFrom="md">
              {sessao.usuario.nome} · {sessao.usuario.papel}
            </Text>
            <BotaoDeTema />
            <Button size="xs" onClick={abrirSenha}>
              Minha senha
            </Button>
            <Button size="xs" onClick={() => void sair()}>
              Sair
            </Button>
          </Group>
        </Group>
      </AppShell.Header>

      <AppShell.Navbar p="xs">
        {/* Em cima as seções; embaixo, o que está vivo agora. A ordem é a
            hierarquia: navegar é o uso comum, o contexto é referência. */}
        <AppShell.Section grow component={ScrollArea}>
          <Stack gap={2}>
            {secoes.map((s) => (
              <NavLink
                key={s.id}
                active={secao === s.id}
                label={s.rotulo}
                description={s.descricao}
                leftSection={s.icone}
                rightSection={s.badge}
                onClick={() => ir(s.id)}
              />
            ))}
          </Stack>
        </AppShell.Section>

        <AppShell.Section>
          <Divider my="xs" />
          <BlocoAgora pendencias={pendencias} aoIrPara={ir} />
        </AppShell.Section>
      </AppShell.Navbar>

      <AppShell.Main>
        <Container size="xl" px={0}>
          <TrocarSenha aberto={trocandoSenha} aoFechar={fecharSenha} />

          {/* Título da seção: a lateral diz onde você está, e o conteúdo
              repete em tamanho de leitura. A home traz o próprio título. */}
          {atual && secao !== 'home' && (
            <Stack gap={0} mb="lg">
              <Text fz={24} fw={600} lh={1.2}>
                {atual.rotulo}
              </Text>
              <Text size="sm" c="dimmed">
                {atual.descricao}
              </Text>
            </Stack>
          )}

          {secao === 'home' && <Home aoIrPara={ir} aoVerConversa={verConversa} />}
          {secao === 'transbordos' && (
            <FilaTransbordos
              capacidades={sessao.capacidades}
              aoAbrirAtendimento={setAtendimentoTecnico}
              aoVerConversa={verConversa}
              aoMudarPendencias={carregarPendencias}
            />
          )}
          {secao === 'conversas' && (
            <Conversas
              capacidades={sessao.capacidades}
              selecionadoInicial={conversaAlvo}
              aoConsumirSelecionado={() => setConversaAlvo(null)}
              aoVerDetalheTecnico={setAtendimentoTecnico}
              aoMudarPendencias={carregarPendencias}
            />
          )}
          {secao === 'faqs' && <ListaFaq capacidades={sessao.capacidades} />}
          {secao === 'lacunas' && (
            <FilaLacunas capacidades={sessao.capacidades} aoIrParaFaqs={() => ir('faqs')} />
          )}
          {secao === 'menu' && <Menu capacidades={sessao.capacidades} />}
          {secao === 'metricas' && <Metricas />}
          {secao === 'pessoas' && <Pessoas capacidades={sessao.capacidades} />}
          {secao === 'envios' && <Envios capacidades={sessao.capacidades} />}
          {secao === 'tags' && <Tags capacidades={sessao.capacidades} />}
          {secao === 'usuarios' && sessao.capacidades.administrar && <Usuarios />}

          {/* A auditoria vem de DENTRO da conversa, sobreposta — não é uma
              seção, e era a navegação que não sabia dizer isso. */}
          {atendimentoTecnico && (
            <DetalheTecnico
              atendimentoId={atendimentoTecnico}
              capacidades={sessao.capacidades}
              aoFechar={() => setAtendimentoTecnico(null)}
            />
          )}
        </Container>
      </AppShell.Main>
    </AppShell>
  );
}

/**
 * O que está vivo agora, no pé da lateral.
 *
 * Sempre visível, em qualquer seção: a informação de que alguém está esperando
 * não pode depender de estar na aba certa. Os números são os mesmos de
 * `/admin/pendencias`, que também alimenta os badges.
 */
function BlocoAgora({
  pendencias,
  aoIrPara,
}: {
  pendencias: Pendencias | null;
  aoIrPara: (secao: 'transbordos' | 'conversas') => void;
}): JSX.Element {
  if (!pendencias) {
    return (
      <Text size="xs" c="dimmed" p="xs">
        Carregando o estado atual…
      </Text>
    );
  }

  const desconhecido = pendencias.conversasDesconhecidas;

  return (
    <Paper withBorder p="xs" radius="md">
      <Stack gap={6}>
        <Text size="xs" tt="uppercase" fw={600} c="dimmed" style={{ letterSpacing: '0.06em' }}>
          Agora
        </Text>

        <LinhaAgora
          rotulo="conversas ativas"
          valor={desconhecido ? '—' : pendencias.conversasAtivas}
          onClick={() => aoIrPara('conversas')}
        />
        <LinhaAgora
          rotulo="com o bot calado"
          valor={desconhecido ? '—' : pendencias.conversasPausadas}
          destaque={!desconhecido && pendencias.conversasPausadas > 0}
          onClick={() => aoIrPara('conversas')}
        />
        <LinhaAgora
          rotulo="transbordos na fila"
          valor={pendencias.transbordos}
          destaque={pendencias.transbordos > 0}
          onClick={() => aoIrPara('transbordos')}
        />

        {desconhecido && (
          <Text size="xs" c="dimmed">
            O estado das conversas vive no Redis e ele não respondeu — “—” aqui significa
            desconhecido, não zero.
          </Text>
        )}
      </Stack>
    </Paper>
  );
}

function LinhaAgora({
  rotulo,
  valor,
  destaque = false,
  onClick,
}: {
  rotulo: string;
  valor: number | string;
  destaque?: boolean;
  onClick: () => void;
}): JSX.Element {
  return (
    <Group
      justify="space-between"
      gap="xs"
      wrap="nowrap"
      onClick={onClick}
      style={{ cursor: 'pointer' }}
    >
      <Text size="xs" c="dimmed">
        {rotulo}
      </Text>
      {/* Número em negrito quando há algo a fazer — e a cor é reforço do
          negrito, nunca o único sinal. */}
      <Text size="sm" ff="monospace" fw={destaque ? 700 : 400} c={destaque ? 'red' : undefined}>
        {valor}
      </Text>
    </Group>
  );
}
